import { useEffect, useRef } from "react";

import type { ToastManager } from "../Toast";

export interface StudioErrorToastNotification {
  readonly signature: string;
  readonly message: string;
}

/**
 * Owns one semantic Studio error Toast across rerenders.
 *
 * A suppressed notification is still consumed so an error already presented by a contextual
 * surface (for example, a camera-change dialog) cannot appear later as a stale generic Toast.
 */
export function useStudioErrorToast({
  manager,
  notification,
  suppressed = false,
}: {
  manager: ToastManager;
  notification: StudioErrorToastNotification | null;
  suppressed?: boolean;
}): void {
  const signatureRef = useRef<string | null>(null);
  const toastIdRef = useRef<string | null>(null);
  const signature = notification?.signature ?? null;
  const message = notification?.message ?? null;

  useEffect(
    () => () => {
      if (toastIdRef.current) manager.dismiss(toastIdRef.current);
      signatureRef.current = null;
      toastIdRef.current = null;
    },
    [manager],
  );

  useEffect(() => {
    if (signature === null || message === null) {
      if (toastIdRef.current) manager.dismiss(toastIdRef.current);
      signatureRef.current = null;
      toastIdRef.current = null;
      return;
    }

    if (suppressed) {
      if (toastIdRef.current) manager.dismiss(toastIdRef.current);
      signatureRef.current = signature;
      toastIdRef.current = null;
      return;
    }

    if (signature === signatureRef.current) return;

    if (toastIdRef.current) manager.dismiss(toastIdRef.current);
    signatureRef.current = signature;
    toastIdRef.current = manager.error(message);
  }, [manager, message, signature, suppressed]);
}
